import axios from 'axios';
import toast from 'react-hot-toast';

// Create axios instance
const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || 'http://localhost:5000/api',
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    // Remove Content-Type for FormData requests (let axios set it automatically)
    if (config.data instanceof FormData) {
      delete config.headers['Content-Type'];
    }
    
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response) {
      const { status, data } = error.response;
      switch (status) {
        case 401:
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          if (window.location.pathname !== '/login') {
            window.location.href = '/login';
          }
          break;
        case 403:
          toast.error('Access denied. Insufficient permissions.');
          break;
        case 404:
          toast.error('Resource not found.');
          break;
        case 422:
          if (data.errors && Array.isArray(data.errors)) {
            data.errors.forEach(err => toast.error(err.msg));
          } else {
            toast.error(data.message || 'Validation failed');
          }
          break;
        case 429:
          toast.error('Too many requests. Please wait a moment and try again.');
          break;
        case 500:
          toast.error('Server error. Please try again later.');
          break;
        default:
          toast.error(data.message || 'An unexpected error occurred');
      }
    } else if (error.request) {
      toast.error('Network error. Please check your connection.');
    } else {
      toast.error('An unexpected error occurred');
    }
    return Promise.reject(error);
  }
);

// API service methods
export const authAPI = {
  login: (credentials) => api.post('/auth/login', credentials),
  logout: () => api.post('/auth/logout'),
  getProfile: () => api.get('/auth/me'),
  updateProfile: (data) => api.put('/auth/profile', data),
  changePassword: (data) => api.put('/auth/change-password', data),
};

export const plansAPI = {
  getAll: (params) => api.get('/plans', { params }),
  getActive: (params) => api.get('/plans/active', { params }),
  getCompanyActive: () => api.get('/plans/company-active'),
  getById: (id) => api.get(`/plans/${id}`),
  create: (data) => api.post('/plans', data),
  update: (id, data) => api.put(`/plans/${id}`, data),
  delete: (id) => api.delete(`/plans/${id}`),
  toggleStatus: (id) => api.patch(`/plans/${id}/toggle-status`),
  seedDefaults: (data) => api.post('/plans/seed-defaults', data),
};

export const companiesAPI = {
  getAll: (params) => api.get('/companies', { params }),
  getById: (id) => api.get(`/companies/${id}`),
  create: (data) => api.post('/companies', data),
  update: (id, data) => api.put(`/companies/${id}`, data),
  delete: (id) => api.delete(`/companies/${id}`),
  updateStatus: (id, status) => api.patch(`/companies/${id}/status`, { status }),
  getCustomFields: (module) => api.get('/companies/custom-fields', { params: { module } }),
};

export const industriesAPI = {
  getAll: (params) => api.get('/industries', { params }),
  getById: (id) => api.get(`/industries/${id}`),
  create: (data) => api.post('/industries', data),
  update: (id, data) => api.put(`/industries/${id}`, data),
  delete: (id) => api.delete(`/industries/${id}`),
  toggleStatus: (id) => api.patch(`/industries/${id}/toggle-status`),
  getCustomFields: (industryId, module) => api.get('/industries/custom-fields', { params: { industryId, module } }),
  seedDefaults: () => api.post('/industries/seed-defaults'),
};

export const usersAPI = {
  getAll: (params) => api.get('/users', { params }),
  getById: (id) => api.get(`/users/${id}`),
  create: (data) => api.post('/users', data),
  update: (id, data) => api.put(`/users/${id}`, data),
  delete: (id) => api.delete(`/users/${id}`),
  updateStatus: (id, status) => api.patch(`/users/${id}/status`, { status }),
};

export const leadsAPI = {
  getAll: (params) => api.get('/leads', { params }),
  getById: (id) => api.get(`/leads/${id}`),
  getByCustomer: (customerId, params) => api.get(`/leads/customer/${customerId}`, { params }),
  getAssignableUsers: () => api.get('/leads/users/assignable'),
  create: (data) => api.post('/leads', data),
  update: (id, data) => api.put(`/leads/${id}`, data),
  updateStage: (id, stageId) => api.put(`/leads/${id}/stage`, { stage: stageId }),
  updateAssignment: (id, assignedTo) => api.put(`/leads/${id}`, { assignedTo }),
  delete: (id) => api.delete(`/leads/${id}`),
  getKanban: (params) => api.get('/leads/kanban', { params }),
  convertToCustomer: (id, data) => api.post(`/leads/${id}/convert`, data),
  uploadAttachment: (id, formData) => api.post(`/leads/${id}/attachments`, formData),
  getAttachments: (id) => api.get(`/leads/${id}/attachments`),
  deleteAttachment: (id, attachmentId) => api.delete(`/leads/${id}/attachments/${attachmentId}`),
  downloadAttachment: (id, attachmentId) => api.get(`/leads/${id}/attachments/${attachmentId}/download`, {
    responseType: 'blob'
  }),
  previewAttachment: (id, attachmentId) => api.get(`/leads/${id}/attachments/${attachmentId}/preview`, {
    responseType: 'blob'
  }),
  getStats: () => api.get('/leads/stats/summary'),
  getCostAnalysis: (params) => api.get('/leads/cost-analysis', { params })
};

export const customersAPI = {
  getAll: (params) => api.get('/customers', { params }),
  getById: (id) => api.get(`/customers/${id}`),
  create: (data) => api.post('/customers', data),
  update: (id, data) => api.put(`/customers/${id}`, data),
  delete: (id) => api.delete(`/customers/${id}`),
  updateStatus: (id, status) => api.patch(`/customers/${id}/status`, { status }),
  uploadAttachment: (id, formData) => api.post(`/customers/${id}/attachments`, formData),
  getAttachments: (id) => api.get(`/customers/${id}/attachments`),
  deleteAttachment: (id, attachmentId) => api.delete(`/customers/${id}/attachments/${attachmentId}`),
  downloadAttachment: (id, attachmentId) => api.get(`/customers/${id}/attachments/${attachmentId}/download`, {
    responseType: 'blob'
  }),
  previewAttachment: (id, attachmentId) => api.get(`/customers/${id}/attachments/${attachmentId}/preview`, {
    responseType: 'blob'
  }),
};

export const followupsAPI = {
  getAll: (params) => api.get('/followups', { params }),
  getById: (id) => api.get(`/followups/${id}`),
  create: (data) => api.post('/followups', data),
  update: (id, data) => api.put(`/followups/${id}`, data),
  delete: (id) => api.delete(`/followups/${id}`),
  // Update status via the generic update endpoint
  updateStatus: (id, data) => api.put(`/followups/${id}`, data),
  // Mark complete uses the server PUT /:id/complete route
  markComplete: (id, data) => api.put(`/followups/${id}/complete`, data),
};

export const filesAPI = {
  upload: (formData) => api.post('/files/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  download: (id) => api.get(`/files/${id}/download`, { responseType: 'blob' }),
  delete: (id) => api.delete(`/files/${id}`),
  getInfo: (id) => api.get(`/files/${id}/info`),
};

export const countriesAPI = {
  getAll: (params) => api.get('/countries/all', { params }),
  getById: (id) => api.get(`/countries/${id}`),
  create: (data) => api.post('/countries', data),
  update: (id, data) => api.put(`/countries/${id}`, data),
  delete: (id) => api.delete(`/countries/${id}`),
};

export const currenciesAPI = {
  getAll: (params) => api.get('/currencies', { params }),
  getById: (id) => api.get(`/currencies/${id}`),
  create: (data) => api.post('/currencies', data),
  update: (id, data) => api.put(`/currencies/${id}`, data),
  delete: (id) => api.delete(`/currencies/${id}`),
};

export const leadStagesAPI = {
  getAll: (params) => api.get('/lead-stages', { params }),
  getMaster: (params) => api.get('/lead-stages', { params: { ...params, isMaster: true } }),
  getById: (id) => api.get(`/lead-stages/${id}`),
  create: (data) => api.post('/lead-stages', data),
  createMaster: (data) => api.post('/lead-stages', { ...data, isMaster: true }),
  update: (id, data) => api.put(`/lead-stages/${id}`, data),
  updateMaster: (id, data) => api.put(`/lead-stages/${id}`, { ...data, isMaster: true }),
  delete: (id) => api.delete(`/lead-stages/${id}`),
  deleteMaster: (id, params) => api.delete(`/lead-stages/${id}`, { params: { ...params, isMaster: true } }),
  updateOrder: (data) => api.put('/lead-stages/reorder', data),
  updateMasterOrder: (data) => api.put('/lead-stages/reorder', { ...data, isMaster: true }),
};

export const performanceGoalsAPI = {
  getAll: (params) => api.get('/performance-goals', { params }),
  getActive: () => api.get('/performance-goals/active'),
  getById: (id) => api.get(`/performance-goals/${id}`),
  create: (data) => api.post('/performance-goals', data),
  update: (id, data) => api.put(`/performance-goals/${id}`, data),
  delete: (id) => api.delete(`/performance-goals/${id}`),
  updateProgress: (id, currentValue) => api.put(`/performance-goals/${id}/progress`, { currentValue }),
};

export const paymentsAPI = {
  // Payment Methods (SuperAdmin)
  getPaymentMethods: () => api.get('/payments/methods'),
  getActivePaymentMethods: () => api.get('/payments/methods/active'),
  createPaymentMethod: (data) => api.post('/payments/methods', data),
  updatePaymentMethod: (id, data) => api.put(`/payments/methods/${id}`, data),
  deletePaymentMethod: (id) => api.delete(`/payments/methods/${id}`),

  // Payments
  getAllPayments: (params) => api.get('/payments', { params }),
  getCompanyPayments: (companyId) => api.get(`/payments/company/${companyId}`),
  createPayment: (data) => api.post('/payments', data),
  updatePaymentStatus: (id, data) => api.put(`/payments/${id}/status`, data),
};

export const dashboardAPI = {
  getSuperAdminDashboard: () => api.get('/dashboard/super-admin'),
  getCompanyAdminDashboard: () => api.get('/dashboard/company-admin'),
  getUserDashboard: () => api.get('/dashboard/user'),
  getCompanyUsage: () => api.get('/dashboard/company-usage'),
  getStorageDetails: () => api.get('/dashboard/storage-details'),
};

export const webhooksAPI = {
  get: () => api.get('/webhooks'),
  create: (data) => api.post('/webhooks', data),
  update: (id, data) => api.put(`/webhooks/${id}`, data),
  delete: (id) => api.delete(`/webhooks/${id}`),
  getLogs: (id) => api.get(`/webhooks/${id}/logs`),
};

export default api;
